import { useState } from "react";
import { Download, Monitor, CheckCircle, AlertCircle, HardDrive, Smartphone } from "lucide-react";
import { colors, fonts } from "../styles/theme";
import { useTenant } from "../contexts/TenantContext";

const MEETING_RECORDER_RELEASE_BASE =
  import.meta.env.VITE_MEETING_RECORDER_RELEASE_BASE ||
  "https://github.com/dev-fgf/fgf-crm/releases/latest/download";

const NOTECLIPPER_RELEASE_BASE =
  import.meta.env.VITE_NOTECLIPPER_RELEASE_BASE ||
  "https://github.com/dev-fgf/FGF-NoteClipper/releases/latest/download";

const DOWNLOADS = [
  {
    id: "meeting-recorder-win",
    name: "Insights Meeting Recorder",
    version: "latest",
    description:
      "Automatically detects Zoom, Teams, and WhatsApp calls, records them via FFmpeg, and uploads recordings to Insights.",
    platform: "Windows",
    releaseAsset: "Insights-Meeting-Recorder-Setup-latest.exe",
    size: "~75 MB",
    requirements: [
      "Windows 10 or later",
      "FFmpeg installed (set path in app settings)",
      "API base URL configured to your Insights instance",
    ],
    instructions: [
      "Download and run the installer",
      "Choose installation directory and complete setup",
      "Open the app — it starts in the system tray",
      "Click Settings and enter your Insights API URL",
      "Set the FFmpeg path (e.g. C:\\ffmpeg\\bin\\ffmpeg.exe)",
      "The app will automatically detect meetings and offer to record",
    ],
    warning:
      "The installer is not code-signed, so Windows SmartScreen may show a warning. Click \"More info\" then \"Run anyway\" to proceed.",
  },
  {
    id: "meeting-recorder-mac",
    name: "Insights Meeting Recorder",
    version: "latest",
    description:
      "Automatically detects Zoom, Teams, and WhatsApp calls, records them via FFmpeg, and uploads recordings to Insights.",
    platform: "macOS",
    releaseAsset: "Insights-Meeting-Recorder-latest.dmg",
    size: "~85 MB",
    requirements: [
      "macOS 12 (Monterey) or later",
      "FFmpeg installed (e.g. via Homebrew: brew install ffmpeg)",
      "API base URL configured to your Insights instance",
    ],
    instructions: [
      "Download the .dmg file",
      "Open the .dmg and drag the app to Applications",
      "On first launch, Gatekeeper will block it — go to System Settings > Privacy & Security and click Open Anyway",
      "Click Settings and enter your Insights API URL",
      "Set the FFmpeg path (e.g. /opt/homebrew/bin/ffmpeg)",
      "The app will automatically detect meetings and offer to record",
    ],
    warning:
      "The app is not notarized, so macOS Gatekeeper will block it on first launch. Go to System Settings > Privacy & Security and click \"Open Anyway\" to bypass this.",
  },
  {
    id: "noteclipper-android",
    name: "FGF NoteClipper",
    version: "latest",
    description:
      "Clip, annotate, and share articles from your news feeds. Syncs with the Insights Platform for team collaboration.",
    platform: "Android",
    icon: "smartphone",
    releaseBase: NOTECLIPPER_RELEASE_BASE,
    releaseAsset: "FGF-NoteClipper-latest.apk",
    size: "~30 MB",
    requirements: [
      "Android 10 or later",
      "Allow installation from unknown sources",
      "Insights account for sync",
    ],
    instructions: [
      "Download the .apk file to your Android device",
      "Open the file — Android will prompt to allow installation from this source",
      "Tap Install and wait for completion",
      "Open the app and sign in with your Insights credentials",
      "Your feeds and clips will sync automatically",
    ],
    warning:
      "The APK is not distributed via Google Play. You will need to enable \"Install unknown apps\" for your browser in Android Settings > Apps > Special access.",
  },
];

export default function Downloads() {
  const tenant = useTenant();
  const sidebarBg = tenant.theme?.sidebar?.bg || colors.sidebar;
  const [expanded, setExpanded] = useState(null);

  return (
    <div>
      <h1
        style={{
          fontFamily: fonts.heading,
          fontSize: 28,
          fontWeight: 700,
          marginBottom: 8,
        }}
      >
        Downloads
      </h1>
      <p style={{ color: "#666", marginBottom: 32 }}>
        Desktop tools for your team. Click a card to see installation
        instructions.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 720 }}>
        {DOWNLOADS.map((app) => {
          const isExpanded = expanded === app.id;
          return (
            <div
              key={app.id}
              style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <div
                onClick={() => setExpanded(isExpanded ? null : app.id)}
                style={{
                  padding: "20px 24px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: sidebarBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {app.icon === "smartphone"
                    ? <Smartphone size={24} color="#fff" />
                    : <Monitor size={24} color="#fff" />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontWeight: 600, fontSize: 16 }}>{app.name}</span>
                    <span
                      style={{
                        fontSize: 12,
                        padding: "2px 8px",
                        borderRadius: 12,
                        background: "#f0f4ff",
                        color: "#4a5568",
                      }}
                    >
                      v{app.version}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        padding: "2px 8px",
                        borderRadius: 12,
                        background: "#f0fff4",
                        color: "#276749",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <HardDrive size={12} /> {app.platform}
                    </span>
                  </div>
                  <p style={{ color: "#666", fontSize: 14, marginTop: 4 }}>
                    {app.description}
                  </p>
                </div>
                <DownloadButton app={app} sidebarBg={sidebarBg} />
              </div>

              {/* Expanded details */}
              {isExpanded && (
                <div
                  style={{
                    padding: "0 24px 20px",
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 24,
                      marginTop: 16,
                    }}
                  >
                    <div>
                      <h4
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          color: "#888",
                          marginBottom: 8,
                        }}
                      >
                        Requirements
                      </h4>
                      {app.requirements.map((req, i) => (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 14,
                            color: "#444",
                            marginBottom: 6,
                          }}
                        >
                          <CheckCircle size={14} color="#48bb78" />
                          {req}
                        </div>
                      ))}
                    </div>
                    <div>
                      <h4
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          color: "#888",
                          marginBottom: 8,
                        }}
                      >
                        Installation Steps
                      </h4>
                      {app.instructions.map((step, i) => (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            gap: 8,
                            fontSize: 14,
                            color: "#444",
                            marginBottom: 6,
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 600,
                              color: sidebarBg,
                              flexShrink: 0,
                            }}
                          >
                            {i + 1}.
                          </span>
                          {step}
                        </div>
                      ))}
                    </div>
                  </div>

                  {app.warning && (
                    <div
                      style={{
                        marginTop: 16,
                        padding: 12,
                        background: "#fffbeb",
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                        fontSize: 13,
                        color: "#92400e",
                      }}
                    >
                      <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                      {app.warning}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DownloadButton({ app, sidebarBg }) {
  const base = app.releaseBase || MEETING_RECORDER_RELEASE_BASE;
  const downloadUrl = `${base}/${encodeURIComponent(app.releaseAsset)}`;
  return (
    <a
      href={downloadUrl}
      onClick={(e) => e.stopPropagation()}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 16px",
        background: sidebarBg,
        color: "#fff",
        borderRadius: 8,
        textDecoration: "none",
        fontSize: 14,
        fontWeight: 500,
        flexShrink: 0,
        whiteSpace: "nowrap",
      }}
    >
      <Download size={16} />
      Download ({app.size})
    </a>
  );
}
